import {
  BarChart3,
  BookOpen,
  Building2,
  CalendarDays,
  CalendarPlus,
  Clock,
  FileKey,
  FileMinus,
  FileSignature,
  FileText,
  FolderKanban,
  Handshake,
  KeyRound,
  LayoutDashboard,
  LifeBuoy,
  Lock,
  NotebookPen,
  PackageCheck,
  Plane,
  Radar,
  Receipt,
  ReceiptText,
  Server,
  User,
  UserPlus,
  Wallet,
  type LucideIcon,
} from 'lucide-react';
import { useCanSee, type Gated } from '@/modules/subscription/api';

/**
 * P2-0: the one list of things a user can create (platform review section 7.2). The global New
 * menu, the command palette's "New …" commands and the contextual New menus on record pages
 * (organization, configuration, contact, ticket: lanes 2-1 to 2-3) all read it, so a create
 * flow is added once and appears everywhere it should.
 *
 * Each action is gated like a nav item (`Gated`: permission, plan module, employee link) and
 * opens a route. A target page opens its form from `?new=1` with `useCreateParam`, or is a
 * `/…/new` page; either may read the context it is given (`?organizationId=` and so on).
 */

export type CreateGroup = 'Service' | 'Clients' | 'Documentation' | 'Delivery' | 'Finance' | 'People' | 'Insights';
export const CREATE_GROUPS: CreateGroup[] = ['Service', 'Clients', 'Documentation', 'Delivery', 'Finance', 'People', 'Insights'];

/** What the page the user is on already knows. Contextual menus pass it; the global menu passes nothing. */
export interface CreateContext {
  organizationId?: string;
  contactId?: string;
  configurationId?: string;
  ticketId?: string;
  projectId?: string;
  dealId?: string;
}

export interface CreateAction extends Gated {
  key: string;
  label: string;
  group: CreateGroup;
  icon: LucideIcon;
  /** Extra words the palette matches ("New contract" also finds "agreement"). */
  keywords?: string[];
  /**
   * The record lives inside an organization: without `context.organizationId` the menu asks for
   * one first. `href` then always receives it.
   */
  needsOrganization?: boolean;
  /** The URL that opens the create flow, pre-filled from whatever context the target reads. */
  href: (ctx: CreateContext) => string;
}

/** `/path?a=1` from the defined params only. */
function to(path: string, params: Record<string, string | undefined> = {}) {
  const q = new URLSearchParams(Object.entries(params).filter((e): e is [string, string] => Boolean(e[1])));
  const s = q.toString();
  return s ? `${path}${path.includes('?') ? '&' : '?'}${s}` : path;
}
const inOrg = (tab: string) => (ctx: CreateContext) => `/organizations/${ctx.organizationId}/${tab}`;

export const CREATE_ACTIONS: CreateAction[] = [
  // Service
  { key: 'ticket', label: 'Ticket', group: 'Service', icon: LifeBuoy, permission: 'tickets.write', href: (c) => to('/tickets/new', { organizationId: c.organizationId }) },
  { key: 'appointment', label: 'Appointment', group: 'Service', icon: CalendarPlus, permission: 'schedule.write', keywords: ['booking', 'schedule'], href: () => '/schedule?new=1' },
  { key: 'time-entry', label: 'Time entry', group: 'Service', icon: Clock, permission: 'time.write', keywords: ['log time'], href: () => '/time?new=1' },
  { key: 'monitored-device', label: 'Monitored device', group: 'Service', icon: Radar, permission: 'monitoring.write', planModule: 'monitoring', needsOrganization: true, keywords: ['monitor'], href: (c) => `${inOrg('monitor')(c)}?new=1` },
  // Clients
  { key: 'organization', label: 'Organization', group: 'Clients', icon: Building2, permission: 'organizations.write', keywords: ['client', 'customer', 'company'], href: () => '/organizations?new=1' },
  { key: 'contact', label: 'Contact', group: 'Clients', icon: User, permission: 'organizations.write', needsOrganization: true, keywords: ['person'], href: (c) => `${inOrg('contacts')(c)}?new=1` },
  { key: 'deal', label: 'Deal', group: 'Clients', icon: Handshake, permission: 'crm.write', keywords: ['opportunity', 'sales'], href: () => '/crm/deals?new=1' },
  { key: 'quote', label: 'Quote', group: 'Clients', icon: FileText, permission: 'billing.write', keywords: ['estimate', 'proposal'], href: (c) => to('/billing/quotes/new', { organizationId: c.organizationId, dealId: c.dealId }) },
  // Documentation
  { key: 'configuration', label: 'Configuration', group: 'Documentation', icon: Server, permission: 'docs.write', needsOrganization: true, keywords: ['device', 'asset', 'server'], href: (c) => `${inOrg('configurations')(c)}?new=1` },
  { key: 'password', label: 'Password', group: 'Documentation', icon: KeyRound, permission: 'passwords.write', needsOrganization: true, keywords: ['credential'], href: (c) => `${inOrg('passwords')(c)}?new=1` },
  { key: 'key', label: 'Key or certificate', group: 'Documentation', icon: FileKey, permission: 'passwords.write', needsOrganization: true, keywords: ['licence key', 'ssl', 'certificate'], href: (c) => `${inOrg('keys')(c)}?new=1` },
  { key: 'document', label: 'Document', group: 'Documentation', icon: FileText, permission: 'docs.write', needsOrganization: true, href: inOrg('documents/new') },
  { key: 'knowledge-article', label: 'Knowledge article', group: 'Documentation', icon: BookOpen, permission: 'docs.write', keywords: ['kb', 'article'], href: () => '/knowledge/new' },
  { key: 'personal-password', label: 'Personal password (My Vault)', group: 'Documentation', icon: Lock, keywords: ['my vault'], href: () => '/passwords/my-vault?new=1' },
  // Delivery
  { key: 'project', label: 'Project', group: 'Delivery', icon: FolderKanban, permission: 'projects.write', href: () => '/projects?new=1' },
  { key: 'contract', label: 'Contract', group: 'Delivery', icon: FileSignature, permission: 'agreements.write', keywords: ['agreement'], href: (c) => to('/agreements/new', { organizationId: c.organizationId }) },
  { key: 'purchase-order', label: 'Purchase order', group: 'Delivery', icon: PackageCheck, permission: 'purchasing.write', keywords: ['po'], href: () => '/purchasing?new=1' },
  // Finance
  { key: 'invoice', label: 'Invoice', group: 'Finance', icon: Receipt, permission: 'billing.write', href: (c) => to('/billing/invoices/new', { organizationId: c.organizationId }) },
  { key: 'credit-note', label: 'Credit note', group: 'Finance', icon: FileMinus, permission: 'billing.write', keywords: ['refund'], href: (c) => to('/billing/credit-notes/new', { organizationId: c.organizationId }) },
  { key: 'bill', label: 'Bill', group: 'Finance', icon: ReceiptText, permission: 'accounting.write', planModule: 'accounting', keywords: ['supplier', 'payable'], href: () => '/payables?new=1' },
  { key: 'rebillable-cost', label: 'Rebillable cost', group: 'Finance', icon: Wallet, permission: 'billing.write', keywords: ['expense'], href: () => '/billing/expenses?new=1' },
  { key: 'journal', label: 'Journal', group: 'Finance', icon: NotebookPen, permission: 'accounting.write', planModule: 'accounting', keywords: ['manual journal'], href: () => '/accounting/journals/new' },
  // People
  { key: 'employee', label: 'Employee', group: 'People', icon: UserPlus, permission: 'hr.write', planModule: 'hr', keywords: ['starter', 'hire'], href: () => '/hr/people?new=1' },
  { key: 'leave-request', label: 'Leave request', group: 'People', icon: Plane, needsEmployee: true, planModule: 'hr', keywords: ['holiday', 'time off'], href: () => '/me/leave?new=1' },
  { key: 'expense-claim', label: 'Expense claim', group: 'People', icon: Wallet, needsEmployee: true, planModule: 'hr', href: () => '/me/expenses?new=1' },
  { key: 'timesheet', label: 'Timesheet', group: 'People', icon: CalendarDays, needsEmployee: true, planModule: 'hr', href: () => '/me/timesheets' },
  // Insights
  { key: 'report', label: 'Report', group: 'Insights', icon: BarChart3, permission: 'reports.read', keywords: ['report builder'], href: () => '/reports/builder' },
  { key: 'dashboard', label: 'Dashboard', group: 'Insights', icon: LayoutDashboard, permission: 'reports.read', href: () => '/?new=1' },
];

/** The URL for an action, or null when it still needs an organization. */
export function createHref(action: CreateAction, ctx: CreateContext = {}): string | null {
  if (action.needsOrganization && !ctx.organizationId) return null;
  return action.href(ctx);
}

/**
 * The actions this user may run, in registry order. Pass `keys` for a contextual menu (in the
 * order given): `useCreateActions(['ticket', 'contact', 'configuration'])`.
 */
export function useCreateActions(keys?: string[]): CreateAction[] {
  const canSee = useCanSee();
  const list = keys ? keys.map((k) => CREATE_ACTIONS.find((a) => a.key === k)).filter((a): a is CreateAction => Boolean(a)) : CREATE_ACTIONS;
  return list.filter(canSee);
}
